import { Box, Button, Flex, HStack, Text } from "@chakra-ui/react";
import { useLocation, useNavigate } from "react-router-dom";
import { useMemo } from "react";
import { IconEditHeader } from "@/components/icons/editHeader";

type EventHeaderProps = {
  eventName?: string;
};

const extractEventId = (pathname: string): string => {
  const matchInline = pathname.match(/\/eventos\/(\d+)\//);
  if (matchInline) return matchInline[1];
  const matchEnd = pathname.match(/\/eventos\/[^/]+\/(\d+)(?:\/|$)/);
  if (matchEnd) return matchEnd[1];
  return "";
};

export const EventHeader = ({ eventName }: EventHeaderProps) => {
  const navigate = useNavigate();
  const location = useLocation();

  const eventId = useMemo(
    () => extractEventId(location.pathname),
    [location.pathname]
  );

  return (
    <Box
      w="100%"
      bg="neutral.500"
      position="sticky"
      top={0}
      zIndex={200}
      boxShadow="0 2px 12px rgba(0,0,0,0.25)"
    >
      <Flex
        px={6}
        h="52px"
        align="center"
        justify="space-between"
        position="relative"
        _after={{
          content: '""',
          position: "absolute",
          bottom: 0,
          left: 0,
          w: "220px",
          h: "1px",
          bgGradient: "linear(to-r, primary.500, transparent)",
          opacity: 0.5,
          pointerEvents: "none",
        }}
      >
        {/* Esquerda: voltar + nome do evento */}
        <HStack spacing={0}>
          <Button
            variant="ghost"
            size="sm"
            color="neutral.300"
            borderRadius={4}
            _hover={{ bg: "whiteAlpha.100", color: "neutral.100" }}
            _active={{ bg: "whiteAlpha.150" }}
            onClick={() => navigate("/eventos")}
          >
            ← Eventos
          </Button>

          <Box px={2}>
            <Box w="1px" h="12px" bg="whiteAlpha.200" />
          </Box>

          <HStack spacing={2}>
            <Box
              w="2px"
              h="14px"
              bg="primary.500"
              borderRadius="2px"
              flexShrink={0}
            />
            <Text
              fontFamily="Inter"
              fontWeight="600"
              fontSize="13px"
              color="neutral.50"
              noOfLines={1}
              maxW="480px"
              letterSpacing="0.01em"
            >
              {eventName ?? (
                <Box
                  as="span"
                  display="inline-block"
                  w="160px"
                  h="11px"
                  bg="whiteAlpha.100"
                  borderRadius="3px"
                  verticalAlign="middle"
                />
              )}
            </Text>
          </HStack>
        </HStack>

        {/* Direita: botão Editar */}
        {eventId && (
          <Button
            size="sm"
            variant="outline"
            borderColor="whiteAlpha.200"
            color="neutral.300"
            fontSize="12px"
            fontWeight="500"
            fontFamily="Inter"
            h="30px"
            px={4}
            gap={2}
            borderRadius={5}
            leftIcon={<IconEditHeader />}
            transition="all 0.18s"
            _hover={{
              bg: "primary.500",
              borderColor: "primary.500",
              color: "neutral.700",
            }}
            _active={{
              bg: "primary.400",
              borderColor: "primary.400",
              color: "neutral.700",
            }}
            onClick={() => navigate(`/eventos/editar/${eventId}`)}
          >
            Editar Evento
          </Button>
        )}
      </Flex>
    </Box>
  );
};
